import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithAuth } from "../api/baseQuery";

export type ComplianceJudgment = "pass" | "partial" | "fail";

export type ComplianceFinding = {
  id: number;
  rule_id: string;
  rule_name: string;
  judgment: ComplianceJudgment;
  description: string;
};

export type ComplianceResultResponse = {
  id: number;
  status: "pending" | "complete" | "failed";
  specialty_used: string | null;
  custom_note_name: string | null;
  findings: ComplianceFinding[];
  pass_count: number;
  review_count: number;
  updated_at: string;
};

export type TrendLabel = "declining" | "stable" | "improving";

export type ComplianceCoachingRow = {
  clinician_id: number;
  display_name: string;
  specialty: string | null;
  avatar_initials: string;
  issue_count: number;
  notes_reviewed?: number;
  capture_rate?: number;
  trend_label: TrendLabel;
  trend_delta_pts: number | null;
};

export type RuleCaptureRow = {
  rule_id: string;
  rule_name: string;
  custom_note_id: number | null;
  custom_note_name: string | null;
  sample_size: number;
  pass_pct: number;
  partial_pct: number;
  fail_pct: number;
  capture_rate: number;
  delta_pts: number | null;
  status: "needs_attention" | "watch" | "on_track";
};

export type TrendPoint = {
  date: string;
  label: string;
  value: number | null;
  sample_size: number;
};

export type ComplianceDashboardResponse = {
  updated_at: string;
  has_active_rules: boolean;
  period: { days: number; start: string; end: string };
  scope: {
    group_name: string | null;
    clinicians_in_group: number;
    clinicians_with_activity: number;
    notes_evaluated: number;
  };
  documentation_health: {
    value: number;
    sample_size: number;
    delta_pts: number;
    prior_value: number;
  };
  notes_reviewed: { value: number; coverage_pct: number; evaluated: number };
  unresolved_issues: { value: number; fail_count: number; partial_count: number };
  rule_capture: RuleCaptureRow[];
  coaching: ComplianceCoachingRow[];
  trend: TrendPoint[];
};

export type ComplianceCliniciansResponse = {
  clinicians: ComplianceCoachingRow[];
  has_active_rules: boolean;
};

export type ClinicianSearchResult = {
  clinician_id: number;
  display_name: string;
  specialty: string | null;
  avatar_initials: string;
};

export type ClinicianSearchResponse = { results: ClinicianSearchResult[] };

export type ClinicianNoteCompliance = {
  status: "pass" | "partial" | "fail" | null;
  fail_count: number;
  partial_count: number;
  pass_count: number;
};

export type ClinicianNoteRow = {
  id: number;
  title: string | null;
  client_name: string | null;
  client_uuid: string | null;
  note_type: number | null;
  note_type_label: string;
  created_at: string;
  status: number;
  is_read: boolean;
  compliance: ClinicianNoteCompliance | null;
};

export type ClinicianDetailResponse = {
  clinician: {
    id: number;
    uuid: string;
    display_name: string;
    email: string;
    specialty: string | null;
    avatar_initials: string;
  };
  has_active_rules: boolean;
  stats: {
    documentation_health: number;
    delta_pts: number | null;
    capture_rate: number;
    issue_count: number;
    notes_reviewed: number;
    trend_label: TrendLabel;
    trend_delta_pts: number | null;
  };
  notes: {
    count: number;
    page: number;
    page_size: number;
    results: ClinicianNoteRow[];
  };
  clients_for_filter: { uuid: string; name: string }[];
};

export type ClinicianClientRow = {
  uuid: string;
  name: string;
  age: number | null;
  last_note_created_at: string | null;
  total_notes: number;
};

export type ClinicianClientsResponse = {
  count: number;
  page: number;
  page_size: number;
  results: ClinicianClientRow[];
};

export type ComplianceTemplateSettings = {
  id: number;
  name: string;
  owner_name: string;
  document_preview: string;
  instructions: string;
};

export type ComplianceSettingsResponse = {
  templates: ComplianceTemplateSettings[];
};

export const complianceApi = createApi({
  reducerPath: "complianceApi",
  tagTypes: ["ComplianceForNote", "ComplianceDashboard", "ComplianceClinicians", "ComplianceSettings"],
  baseQuery: baseQueryWithAuth,
  endpoints: (build) => ({
    getComplianceForNote: build.query<ComplianceResultResponse, number>({
      query: (audioFileId) => `/compliance/note/${audioFileId}/`,
      providesTags: (_result, _err, audioFileId) => [{ type: "ComplianceForNote", id: audioFileId }],
    }),
    getComplianceDashboard: build.query<ComplianceDashboardResponse, { days?: number } | void>({
      query: (arg) => {
        const days = arg && "days" in arg ? arg.days : undefined;
        return `/compliance/dashboard/${days ? `?days=${days}` : ""}`;
      },
      providesTags: [{ type: "ComplianceDashboard", id: "ROOT" }],
    }),
    getComplianceClinicians: build.query<
      ComplianceCliniciansResponse,
      { sort?: string; search?: string; days?: number }
    >({
      query: ({ sort, search, days } = {}) => {
        const params = new URLSearchParams();
        if (sort) params.set("sort", sort);
        if (search) params.set("search", search);
        if (days) params.set("days", String(days));
        const qs = params.toString();
        return `/compliance/clinicians/${qs ? `?${qs}` : ""}`;
      },
      providesTags: [{ type: "ComplianceClinicians", id: "LIST" }],
    }),
    getComplianceClinicianSearch: build.query<ClinicianSearchResponse, { q: string; limit?: number }>({
      query: ({ q, limit }) => {
        const params = new URLSearchParams();
        if (q) params.set("q", q);
        if (limit) params.set("limit", String(limit));
        return `/compliance/clinicians/search/?${params.toString()}`;
      },
    }),
    getComplianceClinicianDetail: build.query<
      ClinicianDetailResponse,
      { userId: number; days?: number; page?: number; pageSize?: number; clientUuid?: string }
    >({
      query: ({ userId, days, page, pageSize, clientUuid }) => {
        const params = new URLSearchParams();
        if (days) params.set("days", String(days));
        if (page) params.set("page", String(page));
        if (pageSize) params.set("page_size", String(pageSize));
        if (clientUuid) params.set("client_uuid", clientUuid);
        const qs = params.toString();
        return `/compliance/clinicians/${userId}/${qs ? `?${qs}` : ""}`;
      },
    }),
    getComplianceClinicianClients: build.query<
      ClinicianClientsResponse,
      { userId: number; page?: number; pageSize?: number }
    >({
      query: ({ userId, page, pageSize }) => {
        const params = new URLSearchParams();
        if (page) params.set("page", String(page));
        if (pageSize) params.set("page_size", String(pageSize));
        const qs = params.toString();
        return `/compliance/clinicians/${userId}/clients/${qs ? `?${qs}` : ""}`;
      },
    }),
    getComplianceSettings: build.query<ComplianceSettingsResponse, void>({
      query: () => "/compliance/settings/",
      providesTags: [{ type: "ComplianceSettings", id: "LIST" }],
    }),
    saveComplianceTemplateInstructions: build.mutation<
      ComplianceTemplateSettings,
      { noteId: number; instructions: string }
    >({
      query: ({ noteId, instructions }) => ({
        url: `/compliance/settings/${noteId}/`,
        method: "PATCH",
        body: { instructions },
      }),
      invalidatesTags: [{ type: "ComplianceSettings", id: "LIST" }],
    }),
  }),
});

export const {
  useGetComplianceForNoteQuery,
  useGetComplianceDashboardQuery,
  useGetComplianceCliniciansQuery,
  useGetComplianceClinicianSearchQuery,
  useGetComplianceClinicianDetailQuery,
  useGetComplianceClinicianClientsQuery,
  useGetComplianceSettingsQuery,
  useSaveComplianceTemplateInstructionsMutation,
} = complianceApi;
